import React from "react";
import {
  Formik,
  FormikActions,
  Form as FormikForm,
  Field,
  FormikProps,
} from "formik";
import { InjectedIntlProps, injectIntl, FormattedMessage } from "react-intl";
import TextInput from "../../text-input";
import Typography from "../../typography";
import InputErrorMessage from "../../input-error-message";

import messages from "./NewPlayerForm.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../../types/styled-arguments";
import RegisterStepButton from "../register-step-button";
import { required } from "../../form-utils";

export interface IFormValues {
  name: string;
  location: string;
  colorHome: string;
  colorAway: string;
}

const Title = styled(({ children }) => (
  <Typography.H5>{children}</Typography.H5>
))((props: IStyledArguments) => {
  const { md } = props.theme.breakpoints.values;

  return {
    marginBottom: 23,
    [`@media (max-width: ${md}px)`]: {
      marginBottom: 27,
    },
  };
});

const ImageHeader = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 1, 1, 1),
  width: "100%",
}));

interface IForm extends InjectedIntlProps {
  onSubmit: (arg: IFormValues) => void;
  name?: string;
  location?: string;
  colorHome?: string;
  colorAway?: string;
}

const StyledForm = styled(FormikForm)(() => ({
  display: "flex",
  flexDirection: "column",
}));

const NewTeamForm = ({
  onSubmit,
  name = "",
  location = "",
  colorHome = "",
  colorAway = "",
  intl,
}: IForm) => {
  return (
    <Formik
      initialValues={{
        name,
        location,
        colorHome,
        colorAway,
      }}
      onSubmit={(values: IFormValues, actions: FormikActions<IFormValues>) => {
        onSubmit(values);
        actions.setSubmitting(false);
      }}
      render={({
        errors,
        touched,
        // values,
        isValid,
      }: // setFieldValue
      FormikProps<IFormValues>) => (
        <>
          <ImageHeader>
            <svg
              height="70px"
              viewBox="-5 0 511 511.99983"
              width="70px"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <linearGradient
                id="a"
                gradientTransform="matrix(.441847 0 0 -.441847 -5.220861 500.084877)"
                gradientUnits="userSpaceOnUse"
                x1="340.4197"
                x2="944.0102"
                y1="319.2481"
                y2="922.8386"
              >
                <stop offset="0" stop-color="#41dfd0" />
                <stop offset="1" stop-color="#ee83ef" />
              </linearGradient>
              <path
                d="m431.179688 337.554688h-34.921876v-34.917969c0-6.429688-5.210937-11.640625-11.640624-11.640625h-46.558594c-6.425782 0-11.636719 5.210937-11.636719 11.640625v34.917969h-34.921875c-6.429688 0-11.640625 5.210937-11.640625 11.640624v46.558594c0 6.429688 5.210937 11.640625 11.640625 11.640625h34.921875v34.917969c0 6.429688 5.210937 11.640625 11.636719 11.640625h46.5625c6.425781 0 11.636718-5.210937 11.636718-11.640625v-34.917969h34.921876c6.429687 0 11.640624-5.210937 11.640624-11.640625v-46.558594c0-6.429687-5.214843-11.640624-11.640624-11.640624zm-11.640626 46.558593h-34.921874c-6.425782 0-11.636719 5.210938-11.636719 11.640625v34.921875h-23.28125v-34.921875c0-6.429687-5.210938-11.640625-11.640625-11.640625h-34.917969v-23.277343h34.917969c6.429687 0 11.640625-5.210938 11.640625-11.640626v-34.921874h23.28125v34.921874c0 6.429688 5.210937 11.640626 11.636719 11.640626h34.921874zm-58.199218-151.316406c-20.101563.019531-39.960938 4.40625-58.199219 12.863281v-108.695312l40.566406 24.328125c5.507813 3.3125 12.660157 1.535156 15.972657-3.976563.15625-.257812.300781-.523437.4375-.796875l34.917968-69.839843c2.503906-5.042969 1.03125-11.15625-3.492187-14.511719l-50.746094-38.050781c-29.535156-22.066407-65.390625-34.0273442-102.257813-34.117188h-80.3125c-36.886718.078125-72.765624 12.039062-102.316406 34.117188l-50.75 38.050781c-4.5 3.371093-5.945312 9.480469-3.433594 14.511719l34.921876 69.839843c2.871093 5.75 9.863281 8.085938 15.613281 5.210938.269531-.136719.539062-.28125.796875-.4375l40.566406-24.328125v165.671875c0 6.425781 5.210938 11.636719 11.640625 11.636719h129.257813c-32.089844 70.09375-1.285157 152.933593 68.8125 185.023437 70.09375 32.089844 152.929687 1.285156 185.019531-68.808594 32.089843-70.09375 1.285156-152.933593-68.808594-185.023437-18.265625-8.359375-38.121094-12.683594-58.207031-12.667969zm-104.757813 47.59375v-255.867187c7.875.988281 15.660157 2.609374 23.277344 4.84375v229.886718c-8.527344 6.152344-16.335937 13.242188-23.277344 21.136719zm-76.824219-252.457031-3.746093-4.652344h44.671875l-3.746094 4.652344c-8.835938 10.269531-24.324219 11.429687-34.589844 2.59375-.925781-.800782-1.792968-1.664063-2.589844-2.59375zm-16.296874 16.425781c6.164062 6.972656 14.269531 11.9375 23.28125 14.257813v232.378906h-23.28125zm46.5625 14.257813c9.007812-2.320313 17.117187-7.285157 23.277343-14.257813v246.636719h-23.277343zm134.882812 76.253906-20.265625-12.164063 26.058594-52.121093 19.160156 14.386718zm-13.144531-78.476563-27.144531 54.300781-1.476563-.886718v-71.398438c8.351563 4.027344 16.304687 8.835938 23.746094 14.363282zm-238.136719-17.984375v71.445313l-1.480469.886719-27.144531-54.300782 4.878906-3.667968c7.441406-5.527344 15.390625-10.335938 23.746094-14.363282zm-41.765625 96.507813-24.957031-49.945313 19.160156-14.375 26.0625 52.121094zm65.042969-105.550781c7.617187-2.234376 15.402344-3.855469 23.28125-4.84375v266.472656h-23.28125zm244.4375 459.503906c-64.285156 0-116.398438-52.113282-116.398438-116.394532 0-64.285156 52.113282-116.398437 116.398438-116.398437s116.398437 52.113281 116.398437 116.398437c-.070312 64.253907-52.144531 116.324219-116.398437 116.394532zm0 0"
                fill="url(#a)"
              />
            </svg>
          </ImageHeader>
          <Title>
            <FormattedMessage {...messages.title} />
          </Title>
          <StyledForm data-testid="add-player-form">
            <Field
              name="name"
              validate={required(intl)}
              error={errors.name && touched.name}
              component={TextInput}
              inputProps={{
                "data-testid": "name",
              }}
              label={intl.formatMessage(messages.name)}
            />
            <InputErrorMessage name="name" />
            <Field
              name="location"
              validate={required(intl)}
              error={errors.location && touched.location}
              component={TextInput}
              inputProps={{
                "data-testid": "location",
              }}
              label={intl.formatMessage(messages.location)}
            />
            <InputErrorMessage name="location" />
            <Field
              name="colorHome"
              validate={required(intl)}
              error={errors.colorHome && touched.colorHome}
              component={TextInput}
              inputProps={{
                "data-testid": "colorHome",
              }}
              label={intl.formatMessage(messages.colorHome)}
            />
            <InputErrorMessage name="colorHome" />
            <Field
              name="colorAway"
              validate={required(intl)}
              error={errors.colorAway && touched.colorAway}
              component={TextInput}
              inputProps={{
                "data-testid": "colorAway",
              }}
              label={intl.formatMessage(messages.colorAway)}
            />
            <InputErrorMessage name="colorAway" />

            <div>
              <RegisterStepButton
                type="submit"
                data-testid="go-next-button"
                disabled={!isValid}
              >
                Next
              </RegisterStepButton>
            </div>
          </StyledForm>
        </>
      )}
    />
  );
};

export default injectIntl(NewTeamForm);
